// src/lib/noteFormats.ts

export type NoteFormatId =
  | "soap"
  | "birp"
  | "dap"
  | "sbar"
  | "aar"
  | "agreement"
  | "freeform";

export const NOTE_FORMAT_IDS = [
  "soap",
  "birp",
  "dap",
  "sbar",
  "aar",
  "agreement",
  "freeform",
] as const satisfies readonly NoteFormatId[];

export interface Localized {
  en: string;
  uk: string;
  pl: string;
  ro: string;
}

export interface NoteFormatSection {
  key: string;
  label: Localized;
  helpText: Localized;
}

export interface NoteFormatDefinition {
  id: NoteFormatId;
  shortLabel: Localized;
  sections: NoteFormatSection[];
}

export const NOTE_FORMATS: Record<NoteFormatId, NoteFormatDefinition> = {
  soap: {
    id: "soap",
    shortLabel: {
      en: "SOAP note",
      uk: "Нотатка SOAP",
      pl: "Notatka SOAP",
      ro: "Notiță SOAP",
    },
    sections: [
      {
        key: "S",
        label: {
          en: "Subjective",
          uk: "Суб'єктивне",
          pl: "Subiektywne",
          ro: "Subiectiv",
        },
        helpText: {
          en: "What the patient reported about their experience and feelings.",
          uk: "Що пацієнт повідомив про свій досвід і почуття.",
          pl: "Co pacjent zgłaszał o swoim doświadczeniu i uczuciach.",
          ro: "Ce a raportat pacientul despre experiența și sentimentele sale.",
        },
      },
      {
        key: "O",
        label: {
          en: "Objective",
          uk: "Об'єктивне",
          pl: "Obiektywne",
          ro: "Obiectiv",
        },
        helpText: {
          en: "What you observed — affect, behavior, presentation.",
          uk: "Що ви спостерігали — афект, поведінка, презентація.",
          pl: "Co zaobserwowałeś — afekt, zachowanie, prezentacja.",
          ro: "Ce ați observat — afect, comportament, prezentare.",
        },
      },
      {
        key: "A",
        label: { en: "Assessment", uk: "Оцінка", pl: "Ocena", ro: "Evaluare" },
        helpText: {
          en: "Your clinical assessment — patterns, formulation, progress.",
          uk: "Ваша клінічна оцінка — патерни, формулювання, прогрес.",
          pl: "Twoja ocena kliniczna — wzorce, formułowanie, postęp.",
          ro: "Evaluarea dvs. clinică — tipare, formulare, progres.",
        },
      },
      {
        key: "P",
        label: { en: "Plan", uk: "План", pl: "Plan", ro: "Plan" },
        helpText: {
          en: "Next steps — interventions, homework, focus for next session.",
          uk: "Наступні кроки — втручання, домашнє завдання, фокус наступної сесії.",
          pl: "Kolejne kroki — interwencje, praca domowa, cel następnej sesji.",
          ro: "Pașii următori — intervenții, teme, focus pentru ședința următoare.",
        },
      },
    ],
  },
  birp: {
    id: "birp",
    shortLabel: {
      en: "BIRP note",
      uk: "Нотатка BIRP",
      pl: "Notatka BIRP",
      ro: "Notiță BIRP",
    },
    sections: [
      {
        key: "B",
        label: {
          en: "Behavior",
          uk: "Поведінка",
          pl: "Zachowanie",
          ro: "Comportament",
        },
        helpText: {
          en: "What the patient did or said in session.",
          uk: "Що пацієнт робив або говорив на сесії.",
          pl: "Co pacjent robił lub mówił na sesji.",
          ro: "Ce a făcut sau a spus pacientul în ședință.",
        },
      },
      {
        key: "I",
        label: {
          en: "Intervention",
          uk: "Втручання",
          pl: "Interwencja",
          ro: "Intervenție",
        },
        helpText: {
          en: "What you did — technique, intervention, redirection.",
          uk: "Що ви зробили — техніка, втручання, перенаправлення.",
          pl: "Co zrobiłeś — technika, interwencja, przekierowanie.",
          ro: "Ce ați făcut — tehnică, intervenție, redirecționare.",
        },
      },
      {
        key: "R",
        label: { en: "Response", uk: "Реакція", pl: "Reakcja", ro: "Răspuns" },
        helpText: {
          en: "How the patient responded to the intervention.",
          uk: "Як пацієнт відреагував на втручання.",
          pl: "Jak pacjent zareagował na interwencję.",
          ro: "Cum a răspuns pacientul la intervenție.",
        },
      },
      {
        key: "P",
        label: { en: "Plan", uk: "План", pl: "Plan", ro: "Plan" },
        helpText: {
          en: "What's next — plan for session and follow-up.",
          uk: "Що далі — план сесії та подальших дій.",
          pl: "Co dalej — plan sesji i dalszych działań.",
          ro: "Ce urmează — plan pentru ședință și urmărire.",
        },
      },
    ],
  },
  dap: {
    id: "dap",
    shortLabel: {
      en: "DAP note",
      uk: "Нотатка DAP",
      pl: "Notatka DAP",
      ro: "Notiță DAP",
    },
    sections: [
      {
        key: "D",
        label: { en: "Data", uk: "Дані", pl: "Dane", ro: "Date" },
        helpText: {
          en: "Objective data and subjective observations from the session.",
          uk: "Об'єктивні дані та суб'єктивні спостереження з сесії.",
          pl: "Obiektywne dane i subiektywne obserwacje z sesji.",
          ro: "Date obiective și observații subiective din ședință.",
        },
      },
      {
        key: "A",
        label: { en: "Assessment", uk: "Оцінка", pl: "Ocena", ro: "Evaluare" },
        helpText: {
          en: "Your clinical assessment — patterns, progress, hypotheses.",
          uk: "Ваша клінічна оцінка — патерни, прогрес, гіпотези.",
          pl: "Twoja ocena kliniczna — wzorce, postęp, hipotezy.",
          ro: "Evaluarea dvs. clinică — tipare, progres, ipoteze.",
        },
      },
      {
        key: "P",
        label: { en: "Plan", uk: "План", pl: "Plan", ro: "Plan" },
        helpText: {
          en: "Next steps — interventions, homework, focus for next session.",
          uk: "Наступні кроки — втручання, домашнє завдання, фокус наступної сесії.",
          pl: "Kolejne kroki — interwencje, praca domowa, cel następnej sesji.",
          ro: "Pașii următori — intervenții, teme, focus pentru ședința următoare.",
        },
      },
    ],
  },
  sbar: {
    id: "sbar",
    shortLabel: {
      en: "SBAR note",
      uk: "Нотатка SBAR",
      pl: "Notatka SBAR",
      ro: "Notiță SBAR",
    },
    sections: [
      {
        key: "S",
        label: {
          en: "Situation",
          uk: "Ситуація",
          pl: "Sytuacja",
          ro: "Situație",
        },
        helpText: {
          en: "The immediate situation — what prompted this conversation.",
          uk: "Поточна ситуація — що спонукало цю розмову.",
          pl: "Bieżąca sytuacja — co skłoniło do tej rozmowy.",
          ro: "Situația imediată — ce a determinat această conversație.",
        },
      },
      {
        key: "B",
        label: { en: "Background", uk: "Передумови", pl: "Tło", ro: "Context" },
        helpText: {
          en: "Relevant history and context leading up to it.",
          uk: "Відповідна історія та контекст, що передували їй.",
          pl: "Istotna historia i kontekst, które ją poprzedzały.",
          ro: "Istoricul și contextul relevant care au precedat-o.",
        },
      },
      {
        key: "A",
        label: { en: "Assessment", uk: "Оцінка", pl: "Ocena", ro: "Evaluare" },
        helpText: {
          en: "Your read on what is going on.",
          uk: "Ваша оцінка того, що відбувається.",
          pl: "Twoja ocena tego, co się dzieje.",
          ro: "Evaluarea ta despre ce se întâmplă.",
        },
      },
      {
        key: "R",
        label: {
          en: "Recommendation",
          uk: "Рекомендація",
          pl: "Zalecenie",
          ro: "Recomandare",
        },
        helpText: {
          en: "What you recommend or the agreed next step.",
          uk: "Що ви рекомендуєте або узгоджений наступний крок.",
          pl: "Co zalecasz lub uzgodniony następny krok.",
          ro: "Ce recomanzi sau următorul pas convenit.",
        },
      },
    ],
  },
  aar: {
    id: "aar",
    shortLabel: {
      en: "After-action note",
      uk: "Розбір після дії",
      pl: "Notatka po działaniu",
      ro: "Notiță după acțiune",
    },
    sections: [
      {
        key: "S",
        label: {
          en: "Situation",
          uk: "Ситуація",
          pl: "Sytuacja",
          ro: "Situație",
        },
        helpText: {
          en: "What was happening and the context at the start.",
          uk: "Що відбувалося та контекст на початку.",
          pl: "Co się działo i jaki był kontekst na początku.",
          ro: "Ce se întâmpla și contextul de la început.",
        },
      },
      {
        key: "A",
        label: {
          en: "Actions taken",
          uk: "Дії",
          pl: "Działania",
          ro: "Acțiuni",
        },
        helpText: {
          en: "What you said and did during the conversation.",
          uk: "Що ви говорили та робили під час розмови.",
          pl: "Co powiedziałeś i zrobiłeś podczas rozmowy.",
          ro: "Ce ai spus și ai făcut în timpul conversației.",
        },
      },
      {
        key: "R",
        label: {
          en: "Response & outcome",
          uk: "Реакція та результат",
          pl: "Reakcja i wynik",
          ro: "Răspuns și rezultat",
        },
        helpText: {
          en: "How the other person responded and how it resolved.",
          uk: "Як відреагувала інша сторона і чим це завершилося.",
          pl: "Jak zareagowała druga osoba i jak się to zakończyło.",
          ro: "Cum a răspuns cealaltă persoană și cum s-a rezolvat.",
        },
      },
      {
        key: "F",
        label: {
          en: "Follow-up",
          uk: "Подальші дії",
          pl: "Dalsze działania",
          ro: "Urmărire",
        },
        helpText: {
          en: "Open items and next steps.",
          uk: "Відкриті питання та наступні кроки.",
          pl: "Otwarte kwestie i kolejne kroki.",
          ro: "Aspecte deschise și pașii următori.",
        },
      },
    ],
  },
  agreement: {
    id: "agreement",
    shortLabel: {
      en: "Agreement summary",
      uk: "Підсумок домовленостей",
      pl: "Podsumowanie ustaleń",
      ro: "Rezumatul acordului",
    },
    sections: [
      {
        key: "P",
        label: {
          en: "Positions",
          uk: "Позиції",
          pl: "Stanowiska",
          ro: "Poziții",
        },
        helpText: {
          en: "What each side stated they wanted.",
          uk: "Що кожна сторона заявила, що хоче.",
          pl: "Czego każda strona się domagała.",
          ro: "Ce a declarat fiecare parte că vrea.",
        },
      },
      {
        key: "I",
        label: {
          en: "Interests",
          uk: "Інтереси",
          pl: "Interesy",
          ro: "Interese",
        },
        helpText: {
          en: "The needs and concerns behind those positions.",
          uk: "Потреби та занепокоєння за цими позиціями.",
          pl: "Potrzeby i obawy kryjące się za tymi stanowiskami.",
          ro: "Nevoile și preocupările din spatele acelor poziții.",
        },
      },
      {
        key: "O",
        label: { en: "Options", uk: "Варіанти", pl: "Opcje", ro: "Opțiuni" },
        helpText: {
          en: "Possibilities discussed during the conversation.",
          uk: "Можливості, обговорені під час розмови.",
          pl: "Możliwości omówione podczas rozmowy.",
          ro: "Posibilitățile discutate în timpul conversației.",
        },
      },
      {
        key: "A",
        label: {
          en: "Agreements & next steps",
          uk: "Домовленості та кроки",
          pl: "Ustalenia i kroki",
          ro: "Acorduri și pași",
        },
        helpText: {
          en: "What was agreed and what happens next.",
          uk: "Що було узгоджено і що буде далі.",
          pl: "Co ustalono i co dalej.",
          ro: "Ce s-a convenit și ce urmează.",
        },
      },
    ],
  },
  freeform: {
    id: "freeform",
    shortLabel: { en: "Note", uk: "Нотатка", pl: "Notatka", ro: "Notiță" },
    sections: [],
  },
};
